import { RequestComponent } from "./request.component";
import { RequestService } from "./request.service";

/****************************************************************************
 * TEMPLATE DEFINITIONS
 ***************************************************************************/
const getCustomToolBarButtons = (request: RequestComponent): any[] => [
    {
        id: 'export',
        title: 'Export filtered requests to excel',
        text: 'Export to Excel',
        iconClass: 'fa fa-fw fa-file-excel-o',
        callback: request.handleExport,
    },
    {
        group: true,
        buttons: [
            {
                id: 'inbox',
                title: 'Display all initial, rework, and rework in progress requests',
                text: 'Request Inbox',
                iconClass: 'fa fa-fw fa-inbox',
                callback: () => request.toggleRequestView(false),
                toggleable: true,
                isSelected: (): boolean => !request.isRequestViewAll
            },
            {
                id: 'submitted',
                title: 'Display all submitted requests',
                text: 'Submitted Requests',
                iconClass: 'fa fa-fw fa-download',
                callback: () => request.toggleRequestView(true),
                toggleable: true,
                isSelected: (): boolean => request.isRequestViewAll
            },
        ]
    },

];

const getColumns = (request: RequestComponent, requestService: RequestService): any[] => [
    {
        field: "RequestNumber",
        title: "Request#",
        width: 160,
        template: {
            type: 'action-link',
            filter: 'multiselect',
            allowCustom: true,
            service: requestService.lookupData.RequestNumber,
            callback: request.onViewDetail
        }
    },
    {
        field: 'ProcessName',
        title: 'Process',
        filterable: true,
        sortable: true,
        template: {
            type: 'multiselect',
            service: requestService.lookupData.ProcessName
        }
    },
    {
        field: "Subject",
        title: "Short Description",
    },
    {
        field: "Detail",
        title: "Full Description",
    },
    {
        field: "Originator",
        title: "Originator",
        filterable: true,
        sortable: true,
        template: {
            type: 'multiselect',
            service: requestService.lookupData.OriginatorName
        }
    },
    {
        field: "RequestStatus",
        title: "Status",
        filterable: true,
        sortable: true,
        template: {
            type: 'multiselect',
            service: requestService.lookupData.RequestStatus
        }
    },
    {
        field: "ModifiedBy",
        title: "Modified By",
        template: {
            type: 'multiselect',
            service: requestService.lookupData.ModifiedBy
        },
        visible: false,
    },
    {
        field: "ModifiedDate",
        title: "Modified Date",
        visible: false,
        template: {
            type: 'date'
        }
    },
    {
        field: "SubmissionDate",
        title: "Submission Date",
        visible: false,
        template: {
            type: 'date'
        }
    },

];


const getFormControls = (request: RequestComponent) => [
    {
        type: 'boolean',
        title: 'Simple Request',
        name: 'IsSimpleRequest',
        stretch: true,
        class: 'inline-flex flex-end',
        callback: request.handleSimpleRequestChange
    },
    {
        type: 'text',
        title: 'Short Description',
        name: 'Subject',
        invalidText: 'Short Description must be between 1 and 65 characters.',
        stretch: true,
    },
    {
        type: 'textarea',
        title: 'Full Description',
        name: 'Detail',
        invalidText: 'Full Description must be between 1 and 1000 characters.',
        stretch: true,
    },
    {
        type: 'number',
        title: 'Estimated Gross Savings of Change *',
        name: 'EstimatedGrossSavings',
    },
    {
        type: 'number',
        title: 'Estimated Cost of Change *',
        name: 'EstimatedCostofChange',
    },
    {
        type: 'text',
        title: 'EWO',
        name: 'EngineeringWorkOrder',
    },
    {
        type: 'text',
        // title: 'PPCR',
        title: 'PFCM',
        name: 'PlantProcessChangeRequest',
    },
    {
        type: 'text',
        title: 'TCR',
        name: 'ToolChangeRequest',
    },
    {
        type: 'boolean',
        title: 'Design Correction / Improvement',
        name: 'DesignCA',
        callback: request.handleDesignCAChange
    },
    {
        type: 'upload',
        name: 'RequestAttachmentDtos',
        title: 'Reference Files',
        autoUpload: false,
        remove: request.handleRemove,
        download: request.getS3Object,
        select: request.handleSelect,
        save: request.handleCommentSave,
        cancel: request.handleCommentCancel,
        comment: true,
        stretch: true,
    },
    {
        type: 'action',
        label: 'Delete Request',
        iconClass: 'fa fa-fw fa-trash',
        name: 'deleteBtn',
        primary: true,
        outline: true,
        danger: true,
        stretch: true,
        class: 'inline-flex flex-end',
        callback: () => request.delete(),
    },
]

const getDetailFormControls = (request: RequestComponent) => [
    {
        type: 'combobox',
        title: 'Authorization Type',
        name: 'AuthorizationTypeId',
        textField: 'Name',
        valueField: 'Id',
        onChange: request.authorizationTypeChange,
        service: request.authorizationTypeService,
    },
    {
        type: 'combobox',
        title: 'Primary Part',
        name: 'PrimaryPartId',
        textField: 'PartNumber',
        valueField: 'Id',
        placeholder: 'Part number',
        service: request.primaryParts,
        search: request.partSearchComponent.handleSearch,
        onChange: request.primaryPartChange
    },
    {
        type: "multiselect",
        title: "Temporary Requests",
        name: "TemporaryRequestIds",
        textField: "Name",
        valueField: "RequestId",
        service: request.filteredTemporaryRequests,
    },
    {
        type: 'action',
        label: 'Select Tooling Components',
        iconClass: 'fa fa-fw fa-wrench',
        name: 'toolingBtn',
        invalidText: request.toolingInvalidText,
        primary: true,
        callback: request.openToolingComponent,
    },
    {
        title: 'Primary Part Details',
        type: 'grid',
        name: 'PrimaryPartId',
        service: request.primaryPart,
        stretch: true,
        columns: [
            {
                field: 'PartNumber',
                title: 'Part#',
            },
            {
                field: 'ProgramName',
                title: 'Program Name',
            },
            {
                field: 'PartType',
                title: 'Part Type',
            },
            {
                field: 'Location',
                title: 'Location',
            },
            {
                field: 'DesignPhase',
                title: 'Design Phase',
            },
            {
                field: 'PTE1UserName',
                title: 'PTE1',
            },
            {
                field: 'PTE2UserName',
                title: 'PTE2',
            },
            {
                field: 'PartStatus',
                title: 'Status',
            }
        ]
    },
    {
        title: 'Impacted Parts',
        type: 'grid',
        name: 'ImpactedPartIds',
        service: request.impactedParts,
        stretch: true,
        columns: [
            {
                field: 'PartNumber',
                title: 'Part#',
            },
            {
                field: 'ProgramName',
                title: 'Program Name',
            },
            {
                field: 'PartType',
                title: 'Part Type',
            },
            {
                field: 'Location',
                title: 'Location',
            },
            {
                field: 'DesignPhase',
                title: 'Design Phase',
            },
            {
                field: 'PTE1UserName',
                title: 'PTE1',
            },
            {
                field: 'PTE2UserName',
                title: 'PTE2',
            },
            {
                field: 'PartStatus',
                title: 'Status',
            }
        ]
    },
    {
        title: 'Location Approvers',
        type: 'grid',
        name: 'LocationApprovers',
        service: request.locationApproverService,
        stretch: true,
        columns: [
            {
                field: 'Location',
                title: 'Location',
            },
            {
                field: 'TM',
                title: 'Technical Manager',
            },
        ]
    },
]

const getFormDialogTabs = (request) => [
    {
        title: "General",
        selected: true,
        formControls: getFormControls(request),
        editForm: request.editForm
    },
    {
        title: "Part / Tooling Details",
        formControls: getDetailFormControls(request),
        editForm: request.editForm
    }
];


export const RequestTemplate = {
    columns: getColumns,
    customToolbarButtons: getCustomToolBarButtons,
    formDialogTabs: getFormDialogTabs
}
